// apps/client/src/assets/builders/tree.ts (trees and rocks, 2026-10-16) — original soft toy trees, replacing the kit trees by catalog id:
//   shape 'puff'  — a round tree: a tapered trunk with a root flare under a cloud of low-poly puffs (the round trees and the fruit tree);
//   shape 'pines' — a grove of gumdrop pines: stacked tiers with a rounded rim and tip (`round` 1 for chubbier, rounder tiers).
// Params (content:assets/world.ts): { shape, seed, height, width, count?, tiers?, round?, fruit?, leaf, trunk }. Each tree is one
// vertex-coloured mesh plus a hidden far model (objectView `farModel`) the outdoor instancer draws for distant instances. A fruit
// tree carries `userData.fruitSpots`: six points on the outside of its canopy, most on the camera's side (+z), where the tree view
// hangs the fruit.
import { rngFrom, type Rng } from '@whiskerton/shared';
import * as THREE from 'three';
import { farModel } from '../../world/objectView';
import { exitPoint, lathe, mergeParts, NATURE_MATERIAL, paintVertices, puff, smoothstep } from '../organic';
import type { CodeBuilder } from '../service';

type Params = Readonly<Record<string, string | number | boolean>>;
const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const colour = (v: unknown, d: string) => new THREE.Color(typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v) ? v : d);
interface Ball { at: THREE.Vector3; r: number }
interface Built { near: THREE.BufferGeometry; far: THREE.BufferGeometry; spots?: [number, number, number][] }

/** Each instanced tree's colour varies by up to this much by where it stands (world/instancer.ts), so a stand is not all clones. */
const TINT_JITTER = 0.07;
/** Lighter toward the sky (DESIGN.md: sky-facing faces up to 14 % lighter), darker underneath. */
const skyShade = (n: THREE.Vector3) => (1 + 0.14 * Math.max(0, n.y)) * (1 - 0.16 * Math.max(0, -n.y));

// ---------- the round tree: a cloud of puffs
/** Where the six fruits hang: [angle round from the camera side (+z), radians; height on the canopy, −1 bottom … 1 top]. */
const FRUIT_AT: readonly (readonly [number, number])[] = [[-1.75, 0.05], [-0.95, -0.3], [-0.3, 0.25], [0.35, -0.2], [1.0, 0.3], [1.8, -0.05]];

function trunkPaint(base: THREE.Color, shadeFrom: number) {
  return (p: THREE.Vector3, n: THREE.Vector3, c: THREE.Color) => {
    // darker at the soil and where the canopy shades it
    const k = (0.72 + 0.28 * smoothstep(0, 0.5, p.y)) * (1 - 0.32 * smoothstep(shadeFrom - 0.5, shadeFrom + 0.3, p.y)) * skyShade(n);
    c.copy(base).multiplyScalar(k);
  };
}

function puffTree(p: Params, rng: Rng): Built {
  const H = num(p.height, 4.3), W = num(p.width, 2.4), k = H / 4.3;
  const leaf = colour(p.leaf, '#5DAE62'), bark = colour(p.trunk, '#8A6A4F');
  const bottom = H * 0.3, ax = W / 2, ay = (H - bottom) / 2, C = new THREE.Vector3(0, bottom + ay, 0);
  // The canopy: a core ball, a ring of five round its waist and two on top, each touching the canopy's ellipsoid from inside.
  const onShell = (az: number, el: number, r: number) =>
    new THREE.Vector3(Math.cos(el) * Math.sin(az) * (ax - r), Math.sin(el) * (ay - r), Math.cos(el) * Math.cos(az) * (ax - r)).add(C);
  const core: Ball = { at: C.clone().setY(C.y + ay * 0.08), r: Math.min(ax, ay) * 0.84 };
  const ring: Ball[] = [], top: Ball[] = [], phase = rng.next() * Math.PI * 2;
  for (let i = 0; i < 5; i++) {
    const r = core.r * (0.62 + rng.next() * 0.1), az = phase + (i / 5) * Math.PI * 2 + (rng.next() - 0.5) * 0.4;
    ring.push({ at: onShell(az, -0.34 + (rng.next() - 0.5) * 0.24, r), r });
  }
  for (let i = 0; i < 2; i++) {
    const r = core.r * (0.56 + rng.next() * 0.1), az = phase + 0.6 + i * Math.PI + (rng.next() - 0.5) * 0.6;
    top.push({ at: onShell(az, 0.85 + rng.next() * 0.3, r), r });
  }
  const balls = [core, ...ring, ...top];
  const dark = leaf.clone().multiplyScalar(0.58).lerp(new THREE.Color('#2F6F55'), 0.2);
  const light = leaf.clone().lerp(new THREE.Color('#B9DA74'), 0.3);
  const tints = balls.map(() => 0.94 + rng.next() * 0.12);
  const canopyPaint = (tint: number) => (q: THREE.Vector3, n: THREE.Vector3, c: THREE.Color) => {
    const t = smoothstep(bottom, H, q.y);
    // Kept clearly darker than the lawn (even the sunlit top), so a tree always reads against the grass behind it.
    c.copy(dark).lerp(leaf, smoothstep(0, 0.62, t)).lerp(light, smoothstep(0.55, 1, t) * 0.35).multiplyScalar(0.92);
    const e = Math.hypot((q.x - C.x) / ax, (q.y - C.y) / ay, (q.z - C.z) / ax);   // 1 on the canopy's skin, less in the crevices
    c.multiplyScalar(tint * skyShade(n) * (0.74 + 0.26 * smoothstep(0.5, 0.95, e)));
  };
  const trunkTop = bottom + ay * 0.7;
  // A chunky toy trunk: a soft root flare, tapering into the canopy.
  const trunkProfile = (s: number): [number, number][] => [[0.38 * k * s, 0], [0.26 * k * s, 0.1 * k], [0.21 * k * s, 0.42 * k], [0.17 * k * s, bottom], [0.1 * k * s, trunkTop]];
  const near = mergeParts([
    paintVertices(lathe(trunkProfile(1), 7, rng.next()), trunkPaint(bark, bottom)),
    ...balls.map((b, i) => paintVertices(puff({ at: b.at, r: b.r, centre: C, bend: 0.45 }, 1), canopyPaint(tints[i]!))),
  ]);
  // Far: the core, three of the ring (a little bigger) and one top puff, coarser, bent further toward one soft ball.
  const farBalls = [core, ring[0]!, ring[2]!, ring[3]!, top[0]!].map((b, i) => (i === 0 ? b : { at: b.at, r: b.r * 1.12 }));
  const far = mergeParts([
    paintVertices(lathe(trunkProfile(1.1), 5), trunkPaint(bark, bottom)),
    ...farBalls.map((b) => paintVertices(puff({ at: b.at, r: b.r, centre: C, bend: 0.6 }, 0), canopyPaint(1))),
  ]);
  if (p.fruit !== true) return { near, far };
  const spots: [number, number, number][] = [], dir = new THREE.Vector3();
  for (const [a, up] of FRUIT_AT) {                               // out from the canopy's centre to where the leaves end
    const el = Math.asin(up) * 0.8;
    dir.set(Math.cos(el) * Math.sin(a), Math.sin(el), Math.cos(el) * Math.cos(a)).normalize();
    const t = exitPoint(C, dir, balls) ?? ax;
    const s = C.clone().addScaledVector(dir, t - 0.05);
    spots.push([s.x, s.y, s.z]);
  }
  return { near, far, spots };
}

// ---------- the grove: gumdrop pines
interface Pine { x: number; z: number; h: number; spin: number; lean: THREE.Euler }
function pineLayout(count: number, H: number, W: number, R: (h: number) => number, rng: Rng): Pine[] {
  const pines: Pine[] = [];
  const lean = () => new THREE.Euler((rng.next() - 0.5) * 0.08, 0, (rng.next() - 0.5) * 0.08);
  if (count <= 1) return [{ x: 0, z: 0, h: H, spin: rng.next(), lean: lean() }];
  // The tallest a little off the middle, the rest round it, each kept inside the grove's spread.
  pines.push({ x: (rng.next() - 0.5) * W * 0.08, z: -W * 0.06, h: H, spin: rng.next(), lean: lean() });
  const phase = rng.next() * Math.PI * 2;
  for (let i = 1; i < count; i++) {
    const h = H * (0.62 + rng.next() * 0.24), a = phase + ((i - 1) / (count - 1)) * Math.PI * 2 + (rng.next() - 0.5) * 0.5;
    const d = W / 2 - R(h) * 1.02;
    pines.push({ x: Math.sin(a) * d, z: Math.cos(a) * d, h, spin: rng.next(), lean: lean() });
  }
  return pines;
}
/** One tier's profile, (radius, height) from its rim's underside to its tip: a gumdrop, with a soft rolled rim, a slope that bulges a
 *  little (more with `round`) and a domed tip, never a spike. */
const tierProfile = (R: number, th: number, round: number, far: boolean): [number, number][] => far
  ? [[R * 0.98, th * 0.04], [R * (0.58 + round * 0.14), th * 0.58], [0, th]]
  : [[R * 0.78, -th * 0.03], [R * 0.99, th * 0.06], [R * (0.92 + round * 0.04), th * (0.19 + round * 0.05)], [R * (0.58 + round * 0.16), th * 0.55],
     [R * (0.26 + round * 0.12), th * 0.84], [0, th]];

function pineGrove(p: Params, rng: Rng): Built {
  const H = num(p.height, 4), W = num(p.width, 4.5), count = Math.max(1, Math.round(num(p.count, 1))), tiers = Math.max(2, Math.round(num(p.tiers, 3)));
  const round = Math.min(1, Math.max(0, num(p.round, 0)));
  const leaf = colour(p.leaf, '#4A9C5E'), bark = colour(p.trunk, '#8A6A4F');
  // Tier j's radius and height shrink by `shrink` a tier; each tier starts halfway up the one below, and the top one ends at the
  // pine's height. Tiers no more than about twice as tall as they are wide read as gumdrops, not spikes.
  const spread = 0.31 + round * 0.05, shrink = 0.2 - round * 0.04, BASE = 0.16 - round * 0.02;   // BASE: the bare trunk below the tiers
  const f = (j: number) => 1 - j * shrink;
  let sum = f(tiers - 1);
  for (let j = 0; j < tiers - 1; j++) sum += f(j) * 0.5;
  const tall = (1 - BASE) / sum;                                  // tier 0's height, as a share of the pine's
  const R = (h: number) => h * spread;
  const pines = pineLayout(count, H, W, R, rng);
  const deep = leaf.clone().multiplyScalar(0.7).lerp(new THREE.Color('#2C6B57'), 0.2), tip = leaf.clone().lerp(new THREE.Color('#B9DC7C'), 0.32);
  const build = (far: boolean) => {
    const parts: THREE.BufferGeometry[] = [];
    for (const pine of pines) {
      const place = new THREE.Matrix4().compose(new THREE.Vector3(pine.x, 0, pine.z), new THREE.Quaternion().setFromEuler(pine.lean), new THREE.Vector3(1, 1, 1));
      const body0 = pine.h * BASE;
      const trunk = lathe([[pine.h * 0.055, 0], [pine.h * 0.042, body0 + pine.h * tall * 0.4]], far ? 4 : 6, pine.spin);
      parts.push(paintVertices(trunk.applyMatrix4(place), trunkPaint(bark, body0)));
      let base = body0;
      for (let j = 0; j < tiers; j++) {
        const top = j === tiers - 1, th = pine.h * tall * f(j), r = R(pine.h) * f(j);
        if (j > 0) base += pine.h * tall * f(j - 1) * 0.5;
        const shade = 0.86 + (j / (tiers - 1)) * 0.2;              // lower tiers deeper green, the top one lightest
        const g = lathe(tierProfile(r, th, top ? Math.min(1, round + 0.35) : round, far), far ? 5 : 7, pine.spin * 6 + j * 0.9)
          .translate(0, base, 0).applyMatrix4(place);
        const from = base;
        parts.push(paintVertices(g, (q, n, c) => {
          const local = (q.y - from) / th;                           // 0 at the rim … 1 at the tip
          c.copy(deep).lerp(leaf, smoothstep(0.02, 0.32, local)).lerp(tip, smoothstep(0.4, 1, local) * 0.4);
          c.multiplyScalar(shade * skyShade(n));
        }));
      }
    }
    return mergeParts(parts);
  };
  return { near: build(false), far: build(true) };
}

export const builder: CodeBuilder = {
  id: 'tree',
  build(p) {
    const rng = rngFrom('tree', String(p.shape ?? 'puff'), num(p.seed, 1));
    const made = p.shape === 'pines' ? pineGrove(p, rng) : puffTree(p, rng);
    const g = new THREE.Group();
    g.name = 'tree';
    const near = new THREE.Mesh(made.near, NATURE_MATERIAL), far = farModel(new THREE.Mesh(made.far, NATURE_MATERIAL));
    near.name = 'leaves'; far.name = 'far';
    for (const m of [near, far]) { m.castShadow = true; m.receiveShadow = true; m.userData.tintJitter = TINT_JITTER; }
    g.add(near, far);
    if (made.spots) g.userData.fruitSpots = made.spots;
    return g;
  },
};
